// Offline queue for recall returns logged on a desktop terminal without internet.
import api from './api';
import { encryptValue, decryptValue } from './offlineCrypto';
import { getSetting, saveSetting, getCachedProduct, upsertProducts } from './offlineDB';

const KEY = 'pending_recall_returns';
let syncing = false;

export const getQueuedRecallReturns = async () => {
  const enc = await getSetting(KEY);
  if (!enc) return [];
  return (await decryptValue(enc)) || [];
};

const saveQueue = async (rows) => saveSetting(KEY, await encryptValue(rows));

// Deduct the returned units from the cached copy so the till stays accurate until sync.
const deductCached = async (productId, batchNumber, qty) => {
  const p = await getCachedProduct(productId);
  if (!p) return;
  let left = qty;
  const target = (p.batches || []).filter((b) => (batchNumber
    ? (b.batch_number || '').toLowerCase() === batchNumber.toLowerCase()
    : (b.recalled || p.recall_locked)));
  for (const b of target) {
    const take = Math.min(left, Math.max(0, b.quantity || 0));
    b.quantity = (b.quantity || 0) - take;
    left -= take;
  }
  p.stock_quantity = Math.max(0, (p.stock_quantity || 0) - qty);
  await upsertProducts([p]);
};

export const queueRecallReturn = async (payload, productName) => {
  const row = {
    ...payload,
    product_name: productName,
    client_ref: `rr_offline_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    logged_offline_at: new Date().toISOString(),
    status: 'pending',
  };
  await saveQueue([...(await getQueuedRecallReturns()), row]);
  await deductCached(payload.product_id, payload.batch_number, Number(payload.quantity));
  return row;
};

export const syncQueuedRecallReturns = async () => {
  if (syncing || !navigator.onLine) return { synced: 0, failed: 0 };
  const rows = await getQueuedRecallReturns();
  if (!rows.length) return { synced: 0, failed: 0 };
  syncing = true;
  let synced = 0;
  let failed = 0;
  const done = new Set();
  const rejected = {};
  try {
    for (const r of rows) {
      if (r.status === 'failed') continue;
      try {
        const { status, error, product_name, ...body } = r;
        await api.post('/recall-returns', body);
        synced += 1;
        done.add(r.client_ref);
      } catch (e) {
        if (!e?.response?.status) continue;
        failed += 1;
        rejected[r.client_ref] = e?.response?.data?.detail || 'Server rejected this return';
      }
    }
    // Re-read so a return queued while this sync ran is not lost.
    const latest = await getQueuedRecallReturns();
    await saveQueue(latest.filter((r) => !done.has(r.client_ref))
      .map((r) => (rejected[r.client_ref] ? { ...r, status: 'failed', error: rejected[r.client_ref] } : r)));
  } finally {
    syncing = false;
  }
  return { synced, failed };
};

export const discardQueuedRecallReturn = async (clientRef) => {
  await saveQueue((await getQueuedRecallReturns()).filter((r) => r.client_ref !== clientRef));
};
